import { useEffect, useRef } from 'react';

const AUTO_OPEN_CONDITIONS = {
    clickCount: 'clickCount',
    pageScroll: 'pageScroll',
    timeDelay: 'timeDelay',
    hasExitIntent: 'hasExitIntent',
};

export const useAutoOpen = (conditions = {}, onOpen) => {
    // Diregard the other auto open conditions when 'hasExitIntent' is set to 'true'
    const ref = conditions.hasExitIntent
        ? useRef([])
        : useRef(
              Object.entries(conditions)
                  .filter((entry) => {
                      const key = entry[0];
                      const value = entry[1];

                      // 'hasExitIntent' should not be included together with the other
                      // auto open triggers
                      if (key === AUTO_OPEN_CONDITIONS.hasExitIntent) {
                          return false;
                      }

                      if (key === AUTO_OPEN_CONDITIONS.timeDelay) {
                          return true;
                      }

                      return value !== 0 && value !== null;
                  })
                  .map((entry) => {
                      const key = entry[0];
                      const value = entry[1];

                      return {
                          name: key,
                          value: value,
                          isFullfilled: false,
                      };
                  })
          );

    const timeoutIdRef = useRef(null);

    const removeAutoOpenEventHandlers = () => {
        document.removeEventListener('click', handleClick);
        window.removeEventListener('scroll', handleScroll);
        timeoutIdRef && clearTimeout(timeoutIdRef.current);
    };

    const checkShouldOpen = () => {
        const allFullfilled = ref.current.every(
            (condition) => condition.isFullfilled
        );

        if (allFullfilled && typeof onOpen === 'function') {
            document.removeEventListener('click', handleClick);
            window.removeEventListener('scroll', handleScroll);
            timeoutIdRef && clearTimeout(timeoutIdRef.current);
            onOpen();
            removeOnMouseOutEvent();
        }
    };

    const handleClick = () => {
        const condition = ref.current.find(
            (condition) => condition.name === AUTO_OPEN_CONDITIONS.clickCount
        );

        if (!condition) {
            document.removeEventListener('click', handleClick);
            return;
        }

        condition.clicks =
            condition.clicks !== undefined ? condition.clicks + 1 : 1;

        condition.isFullfilled = condition.clicks >= condition.value;

        if (condition.isFullfilled) {
            document.removeEventListener('click', handleClick);
        }

        checkShouldOpen();
    };

    const handleScroll = () => {
        const condition = ref.current.find(
            (condition) => condition.name === AUTO_OPEN_CONDITIONS.pageScroll
        );

        if (!condition) {
            window.removeEventListener('scroll', handleScroll);
            return;
        }

        const scrollTop = window.scrollY;
        const docHeight = document.body.offsetHeight;
        const winHeight = window.innerHeight;
        const scrollPercent = scrollTop / (docHeight - winHeight);
        const scrollPercentRounded = Math.round(scrollPercent * 100);

        condition.currentScrollPercent = scrollPercentRounded;

        condition.isFullfilled =
            condition.currentScrollPercent >= condition.value;

        if (condition.isFullfilled) {
            window.removeEventListener('scroll', handleScroll);
        }

        checkShouldOpen();
    };

    useEffect(() => {
        for (const condition of ref.current) {
            if (condition.name === AUTO_OPEN_CONDITIONS.timeDelay) {
                timeoutIdRef.current = setTimeout(() => {
                    const condition = ref.current.find(
                        (condition) =>
                            condition.name === AUTO_OPEN_CONDITIONS.timeDelay
                    );

                    if (condition) {
                        condition.isFullfilled = true;
                        checkShouldOpen();
                    }
                }, condition.value * 1000);
            } else if (condition.name === AUTO_OPEN_CONDITIONS.clickCount) {
                document.addEventListener('click', handleClick);
            } else if (condition.name === AUTO_OPEN_CONDITIONS.pageScroll) {
                window.addEventListener('scroll', handleScroll);
            }
        }

        return () => {
            removeAutoOpenEventHandlers();
        };
    }, []);

    const removeOnMouseOutEvent = () =>
        document.removeEventListener('mouseout', onMouseOut);

    const onMouseOut = (event) => {
        if (!conditions.hasExitIntent) {
            removeOnMouseOutEvent();
        }

        const shouldShowExitIntent =
            !event.toElement && !event.relatedTarget && event.clientY < 10;

        if (shouldShowExitIntent && conditions.hasExitIntent) {
            onOpen();
            removeOnMouseOutEvent();
            removeAutoOpenEventHandlers();
        }
    };
    useEffect(() => {
        document.addEventListener('mouseout', onMouseOut);

        return () => {
            removeOnMouseOutEvent();
        };
    }, []);

    return {
        removeAutoOpenEventHandlers,
    };
};
